import { TGetContext } from '@api/lendingAPI/lapi.types';
import { useGetContext } from '@api/lendingAPI/lapiHooks';
import {
    fetchGetStateByStateKey,
    GetStateResponse
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import {
    TDeserialisedPeople,
    TResolutionMethodsSerialisedStatuses
} from '../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';
import { hasIDVerseSplitTest } from '../../../modals/idCheckVerificationModal/IdCheckVerificationModalHelper';
import { TSerialisedPeople } from '../../../modals/personModal/PersonModal.types';
import {
    TCreditCardOnboardingRequirements,
    useGetOnboardingRequirements
} from '../onboardingChecks/useGetOnboardingRequirements';

type TIdCheckResponse = {
    peopleToVerify: TDeserialisedPeople;
    id_verification_provider_name: 'Entrust (Onfido)' | 'IDVerse';
};

export const useGetPeopleToVerify = (): {
    idCheckResponse: TIdCheckResponse | undefined;
} => {
    const stateKey = useStateKey();

    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey }),
        suspense: true
    });
    const { onboardingRequirements } = useGetOnboardingRequirements();
    const { data: site_context } = useGetContext();

    if (!customerState?.data?.application?.people) {
        throw new Error('Application people data is required');
    }

    const idCheckResponse = deserialiseIdCheckResponse(
        customerState.data,
        onboardingRequirements,
        site_context
    );
    return { idCheckResponse };
};

const deserialiseIdCheckResponse = (
    customerState: GetStateResponse['data'],
    onboardingRequirements: TCreditCardOnboardingRequirements,
    site_context: TGetContext['data']
): TIdCheckResponse | undefined => {
    const onfidoResolutionMethod = _.find(onboardingRequirements, {
        recommended_resolution_method: 'onfido_check'
    });
    const onfidoVideoResolutionMethod = _.find(onboardingRequirements, {
        recommended_resolution_method: 'onfido_video_check'
    });
    const idverseCheckResolutionMethod = _.find(onboardingRequirements, {
        recommended_resolution_method: 'idverse_check'
    });

    if (
        !onfidoResolutionMethod &&
        !onfidoVideoResolutionMethod &&
        !idverseCheckResolutionMethod
    )
        return undefined;

    const onfidoChecks = onfidoResolutionMethod?.resolution_methods;
    const onfidoStatuses = onfidoChecks
        ? _.find(onfidoChecks, { type: 'onfido_check' })?.items
        : undefined;

    const onfidoVideoChecks = onfidoVideoResolutionMethod?.resolution_methods;
    const onfidoVideoStatuses = onfidoVideoChecks
        ? _.find(onfidoVideoChecks, { type: 'onfido_video_check' })?.items
        : undefined;

    const idverseChecks = idverseCheckResolutionMethod?.resolution_methods;
    const idverseStatuses = idverseChecks
        ? _.find(idverseChecks, { type: 'idverse_check' })?.items
        : undefined;

    const onlyApplicantsGuarantors = _.chain(customerState.application!.people)
        .filter((person) => {
            const roles = person?.roles ?? [];
            const isApplicant = roles.indexOf('applicant') > -1;
            const isGuarantor = roles.indexOf('guarantor') > -1;

            return isApplicant || isGuarantor;
        })
        .value();

    const id_verification_provider_name = hasIDVerseSplitTest(site_context)
        ? 'IDVerse'
        : 'Entrust (Onfido)';

    return {
        peopleToVerify: deserialisePeople(
            onlyApplicantsGuarantors,
            onfidoStatuses,
            onfidoVideoStatuses,
            idverseStatuses
        ),
        id_verification_provider_name
    };
};

const deserialisePeople = (
    people: TSerialisedPeople,
    onfidoStatuses: TResolutionMethodsSerialisedStatuses,
    onfidoVideoStatuses: TResolutionMethodsSerialisedStatuses,
    idverseStatuses: TResolutionMethodsSerialisedStatuses
): TDeserialisedPeople => {
    return _.map(people, (person) => {
        return {
            uid: person.uid,
            title: person.title!,
            firstName: person.first_name!,
            lastName: person.last_name!,
            roles: person.roles,
            phones: deserialisePhones(person.phones),
            idCheckStatus: getIdCheckStatus(
                onfidoStatuses,
                onfidoVideoStatuses,
                idverseStatuses,
                person.uid
            ),
            addPhoneNumberPending: false
        };
    });
};

const getIdCheckStatus = (
    onfidoStatusList: TResolutionMethodsSerialisedStatuses,
    onfidoVideoStatusList: TResolutionMethodsSerialisedStatuses,
    idverseStatusList: TResolutionMethodsSerialisedStatuses,
    uid: string
) => {
    const onfidoStatus = _.find(onfidoStatusList, { name: uid });
    const onfidoVideoStatus = _.find(onfidoVideoStatusList, { name: uid });
    const idverseStatus = _.find(idverseStatusList, { name: uid });

    if (onfidoStatus || idverseStatus) {
        if (
            (onfidoStatus?.status === 'completed' &&
                (onfidoVideoStatus?.status ?? 'completed') === 'completed') ||
            idverseStatus?.status === 'completed'
        ) {
            return 'Verified';
        }
        if (
            onfidoStatus?.status === 'pending' ||
            onfidoVideoStatus?.status === 'pending' ||
            idverseStatus?.status === 'pending'
        ) {
            return 'Verifying your check';
        }
        if (
            onfidoStatus?.status === 'error' ||
            onfidoVideoStatus?.status === 'error' ||
            idverseStatus?.status === 'error'
        ) {
            return 'Something went wrong';
        }
        return 'Not started yet';
    } else {
        return 'Something went wrong';
    }
};

const deserialisePhones = (
    phones: NonNullable<TSerialisedPeople>[number]['phones']
) => {
    const deserialisedPhones: {
        uid?: string;
        label?: string;
        value?: string | null;
        type?: 'primary' | 'secondary' | 'ADD_NEW';
    }[] = _.map(phones, (phone) => {
        return {
            uid: phone.uid,
            label: phone.number,
            value: phone.number
        };
    });

    deserialisedPhones.push({
        type: 'ADD_NEW',
        label: 'Add new',
        value: null
    });

    return deserialisedPhones;
};
